import * as React from "react";
import {
  addMonths,
  endOfQuarter,
  endOfMonth,
  format,
  startOfQuarter,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import { formatDateForAPI } from "utils/utils";
import classNames from "classnames";
import { cn } from "ui/lib/utils";
import { CircleX, ChevronDown } from "lucide-react";

const today = new Date();

export const last7Days = {
  from: subDays(today, 6),
  to: today,
};

const yesterday = {
  from: subDays(today, 1),
  to: subDays(today, 1),
};

const last30Days = {
  from: subDays(today, 29),
  to: today,
};
const monthToDate = {
  from: startOfMonth(today),
  to: today,
};
const lastMonth = {
  from: startOfMonth(subMonths(today, 1)),
  to: endOfMonth(subMonths(today, 1)),
};
const thisMonth = {
  from: startOfMonth(today),
  to: endOfMonth(today),
};
const currentMonth = today.getMonth();
const currentYear = today.getFullYear();
const startFyYear = currentMonth >= 3 ? currentYear : currentYear - 1;
const financialYear = {
  from: new Date(startFyYear, 3, 1),
  to: new Date(startFyYear + 1, 2, 31),
};
const nextMonth = {
  from: startOfMonth(addMonths(today, 1)),
  to: endOfMonth(addMonths(today, 1)),
};
const lastQuarter = {
  from: startOfQuarter(subMonths(today, 3)),
  to: endOfQuarter(subMonths(today, 3)),
};
const nextQuarter = {
  from: startOfMonth(addMonths(today, 1)),
  to: endOfMonth(addMonths(today, 3)),
};

const presets = {
  today: { from: today, to: today },
  yesterday,
  last7Days,
  last30Days,
  monthToDate,
  lastMonth,
  thisMonth,
  nextMonth,
  lastQuarter,
  nextQuarter,
  financialYear,
};

export default function CalendarDatePicker({
  preset = true,
  stack = false,
  date,
  setDate,
  disableFutureDates = false,
  className = "",
  disabled = true,
  placeholder,
  ...props
}) {
  const parseDate = (d) => {
    if (!d) return "";
    if (typeof d === "string" && d.match(/^\d{4}-\d{2}-\d{2}/)) {
      return d.substring(0, 10);
    }
    try {
      const parsed = new Date(d);
      if (!isNaN(parsed.getTime())) {
        return format(parsed, "yyyy-MM-dd");
      }
    } catch (e) {
      // ignore
    }
    return "";
  };

  const fromDateStr = parseDate(date?.from);
  const toDateStr = parseDate(date?.to);

  const handleFromChange = (e) => {
    const val = e.target.value;
    if (val) {
      const [year, month, day] = val.split("-");
      const localDate = new Date(year, month - 1, day);
      setDate({
        from: formatDateForAPI(localDate),
        to: date?.to,
      });
    } else {
      setDate({
        from: undefined,
        to: date?.to,
      });
    }
  };

  const handleToChange = (e) => {
    const val = e.target.value;
    if (val) {
      const [year, month, day] = val.split("-");
      const localDate = new Date(year, month - 1, day);
      setDate({
        from: date?.from,
        to: formatDateForAPI(localDate),
      });
    } else {
      setDate({
        from: date?.from,
        to: undefined,
      });
    }
  };

  const currentPresetKey = React.useMemo(() => {
    if (!fromDateStr || !toDateStr) return "";
    for (const [key, preset] of Object.entries(presets)) {
      if (
        formatDateForAPI(preset.from) === fromDateStr &&
        formatDateForAPI(preset.to) === toDateStr
      ) {
        return key;
      }
    }
    return "";
  }, [fromDateStr, toDateStr]);

  const handlePresetChange = (e) => {
    const val = e.target.value;
    if (val && presets[val]) {
      const { from, to } = presets[val];
      setDate({
        from: formatDateForAPI(from),
        to: formatDateForAPI(to),
      });
    }
  };

  const maxDate = disableFutureDates ? format(new Date(), "yyyy-MM-dd") : undefined;

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDate({ from: undefined, to: undefined });
  };

  const renderPresets = () => (
    <div className={cn("relative flex items-center w-35", stack && "w-full")}>
      <select
        className={cn(
          "flex h-9 w-full appearance-none rounded-md border border-input bg-transparent px-2 py-1 pr-8 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        )}
        onChange={handlePresetChange}
        value={currentPresetKey}
      >
        <option value="" disabled>Select</option>
        <option value="today">Today</option>
        <option value="yesterday">Yesterday</option>
        <option value="last7Days">Last 7 days</option>
        <option value="last30Days">Last 30 days</option>
        <option value="monthToDate">Month to date</option>
        <option value="lastMonth">Last month</option>
        <option value="thisMonth">This month</option>
        <option value="nextMonth">Next month</option>
        <option value="lastQuarter">Last quarter</option>
        <option value="nextQuarter">Next quarter</option>
        <option value="financialYear">Financial year</option>
      </select>
      {currentPresetKey ? (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
        >
          <CircleX className="h-4 w-4" />
        </button>
      ) : (
        <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
          <ChevronDown className="h-4 w-4" />
        </div>
      )}
    </div>
  );

  return (
    <div className={cn("flex gap-2", stack ? "flex-col" : "items-center", className)} {...props}>
      {!stack && preset && renderPresets()}
      <div className={cn("flex items-center gap-2", stack ? "w-full" : "flex-1")}>
        <input
          type="date"
          className={classNames(
            "flex h-9 flex-1 rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            "relative [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-2 [&::-webkit-calendar-picker-indicator]:cursor-pointer",
            !fromDateStr && "text-muted-foreground"
          )}
          value={fromDateStr}
          max={maxDate}
          onChange={handleFromChange}
        />
        <span className="text-muted-foreground">-</span>
        <input
          type="date"
          className={classNames(
            "flex h-9 flex-1 rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            "relative [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-2 [&::-webkit-calendar-picker-indicator]:cursor-pointer",
            !toDateStr && "text-muted-foreground"
          )}
          value={toDateStr}
          min={fromDateStr || undefined}
          max={maxDate}
          onChange={handleToChange}
        />
      </div>
      {stack && preset && renderPresets()}
    </div>
  );
}
